// File: serviceRevenueService.ts

export interface ServiceRevenue {
  service_id: number;
  service_name: string;
  service_type: string;
  provider_name: string;
  equipment_name: string;
  year: number;
  month: number;
  base_fee: number;
  total_revenue: number;
  clinic_amount: number;
  provider_amount: number;
  owner_amount: number;
  referrer_amount: number;
  charity_amount: number;
  total_cost: number;
  profit: number;
  profit_margin: number;
  transaction_count: number;
  average_revenue_per_transaction: number;
  custom_priced_count: number;
  original_priced_count: number;
}

export interface ServiceComparison {
  service_id: number;
  service_name: string;
  service_type: string;
  provider_name: string;
  equipment_name: string;
  base_fee: number;
  total_revenue: number;
  total_cost: number;
  profit: number;
  profit_margin: number;
  transaction_count: number;
  average_revenue_per_transaction: number;
}

export interface MonthlyRevenueResponse {
  success: boolean;
  year: number;
  month: number;
  month_name: string;
  summary: {
    total_revenue: number;
    total_cost: number;
    total_profit: number;
    total_transactions: number;
    service_count: number;
    average_profit_per_service: number;
    average_profit_margin: number;
  };
  service_revenue: ServiceRevenue[];
}

export interface ServiceYearlyRevenue {
  service_id: number;
  service_name: string;
  year: number;
  monthly_breakdown: {
    month: number;
    month_name: string;
    total_revenue: number;
    total_cost: number;
    profit: number;
    transaction_count: number;
  }[];
  yearly_total_revenue: number;
  yearly_total_cost: number;
  yearly_profit: number;
  yearly_profit_margin: number;
  total_transactions: number;
}

export interface ProfitabilityAnalysis {
  period: {
    start_date: string;
    end_date: string;
  };
  summary: {
    total_services: number;
    profitable_services: number;
    unprofitable_services: number;
    total_revenue: number;
    total_cost: number;
    total_profit: number;
    average_profit_margin: number;
  };
  most_profitable: ServiceComparison[];
  least_profitable: ServiceComparison[];
}

export interface ActiveService {
  id: number;
  name: string;
  service_type: string;
  provider_name: string;
  equipment_name: string;
  base_fee: number;
}

export interface CustomPricingStats {
  service_id: number;
  service_name: string;
  total_transactions: number;
  custom_priced_count: number;
  original_priced_count: number;
  custom_pricing_percentage: number;
  total_savings_given: number;
  average_savings: number;
}

class ServiceRevenueService {
  private baseURL = '/api/service-revenue';

  async getMonthlyRevenue(year: number, month: number): Promise<MonthlyRevenueResponse> {
    const response = await fetch(`${this.baseURL}/monthly?year=${year}&month=${month}`);
    if (!response.ok) {
      throw new Error('Failed to fetch monthly revenue');
    }
    return response.json();
  }

  async getServiceComparison(startDate: string, endDate: string): Promise<{
    success: boolean;
    period: { start_date: string; end_date: string };
    summary: {
      total_revenue: number;
      total_cost: number;
      total_profit: number;
      total_transactions: number;
      service_count: number;
      most_profitable: ServiceComparison;
      average_profit_margin: number;
    };
    service_comparison: ServiceComparison[];
  }> {
    const response = await fetch(
      `${this.baseURL}/comparison?start_date=${startDate}&end_date=${endDate}`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch service comparison');
    }
    return response.json();
  }

  async getServiceYearlyRevenue(serviceId: number, year: number): Promise<{
    success: boolean;
    data: ServiceYearlyRevenue;
  }> {
    const response = await fetch(`${this.baseURL}/${serviceId}/yearly?year=${year}`);
    if (!response.ok) {
      throw new Error('Failed to fetch yearly revenue');
    }
    return response.json();
  }

  async getProfitabilityAnalysis(startDate: string, endDate: string): Promise<{
    success: boolean;
    data: ProfitabilityAnalysis;
  }> {
    const response = await fetch(
      `${this.baseURL}/profitability?start_date=${startDate}&end_date=${endDate}`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch profitability analysis');
    }
    return response.json();
  }

  async getServiceCustomPricingStats(
    serviceId: number,
    startDate: string,
    endDate: string
  ): Promise<{
    success: boolean;
    period: { start_date: string; end_date: string };
    data: CustomPricingStats;
  }> {
    const response = await fetch(
      `${this.baseURL}/${serviceId}/custom-pricing-stats?start_date=${startDate}&end_date=${endDate}`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch custom pricing stats');
    }
    return response.json();
  }

  async getActiveServices(): Promise<{
    success: boolean;
    services: ActiveService[];
  }> {
    const response = await fetch(`${this.baseURL}/active-services`);
    if (!response.ok) {
      throw new Error('Failed to fetch active services');
    }
    return response.json();
  }

  async getDashboardData(year: number, month: number): Promise<{
    success: boolean;
    current_period: {
      year: number;
      month: number;
      month_name: string;
    };
    monthly_revenue: ServiceRevenue[];
    yearly_comparison: ServiceComparison[];
    profitability_analysis: ProfitabilityAnalysis;
    top_profitable: ServiceComparison[];
    summary: {
      monthly_total_revenue: number;
      monthly_total_profit: number;
      yearly_total_revenue: number;
      yearly_total_profit: number;
      active_service_count: number;
    };
  }> {
    const response = await fetch(`${this.baseURL}/dashboard?year=${year}&month=${month}`);
    if (!response.ok) {
      throw new Error('Failed to fetch dashboard data');
    }
    return response.json();
  }
}

export const serviceRevenueService = new ServiceRevenueService();